import React, { useCallback } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { InlineMessage } from 'ui-components/components/inline-message';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import Global from '../../../../../../store/global-selectors';
import messages from '../../messages';
import DeliveryOptionsItemPropType from '../delivery-option-item-prop-type';
import style from './edit-delivery-option-page.module.less';
import ShippingServiceSelector from '../../../../../../components/shipping-service-selector';
import useProviderOrCarrierGroups from '../../../../../../components/shipping-service-selector/group-hooks/use-provider-or-carrier-groups';
import { IS_UNIQUE } from '../../../../../../constants/yup-constants';
import { ShippingServicePropType } from '../../../../../../prop-types/shipping-service';
import RemovableService from '../removable-service';

export const PudoRateOptions = ({
  flatRateValue = '',
  feeThresholdValue = '',
  item,
  setItem,
  validationErrors = emptyObject,
  services = emptyArray,
  pudoServices = emptyArray,
}) => {
  const providerOrCarrierGroups = useProviderOrCarrierGroups(pudoServices);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const onFlatRateInputChangeCallback = useCallback(
    (value) => {
      setItem((_item) => ({
        ..._item,
        flatRate: {
          ..._item.flatRate,
          code: sellerCurrencyCode,
          value: parseFloat(value),
        },
      }));
    },
    [sellerCurrencyCode, setItem],
  );

  const onFeeThresholdInputChangeCallback = useCallback(
    (value) => {
      setItem((_item) => ({
        ..._item,
        feeThreshold: {
          ..._item.feeThreshold,
          enabled: !Number.isNaN(parseFloat(value)),
          currencyCode: sellerCurrencyCode,
          thresholdAmount: parseFloat(value),
        },
      }));
    },
    [sellerCurrencyCode, setItem],
  );

  const onAddServiceClickCallback = useCallback(
    ({ serviceId, sellerLabelProviderId }) => {
      setItem({
        ...item,
        services: [
          ...item.services,
          {
            serviceId,
            sellerProviderId: sellerLabelProviderId,
          },
        ],
      });
    },
    [setItem, item],
  );

  const onServiceDisplayRemoveCallback = useCallback(
    (index) => {
      setItem({
        ...item,
        services: item.services.filter((service, serviceIndex) => serviceIndex !== index),
      });
    },
    [item, setItem],
  );

  const onSellerProviderIdChangeCallback = useCallback(
    (index, sellerProviderId) => {
      setItem({
        ...item,
        services: item.services.map((service, serviceIndex) => {
          if (serviceIndex !== index) {
            return service;
          }
          return {
            ...service,
            sellerProviderId,
          };
        }),
      });
    },
    [setItem, item],
  );

  return (
    <div className={style['edit-delivery-options-section']}>
      <div className={classnames(style['live-rate-section'], style['service-dropdown-section'])}>
        <div className={style['service-title']}>
          <FormattedMessage {...messages.selectService} />
        </div>
        <div className={style['section-controls']}>
          <ShippingServiceSelector
            defaultEmptyText={messages.shippingServiceDropdownPlaceholder}
            onSelected={onAddServiceClickCallback}
            serviceGroups={providerOrCarrierGroups}
          />
          {validationErrors?.services?.type === IS_UNIQUE && (
            <div className={style['services-error']}>
              <InlineMessage type='error'>
                <FormattedMessage {...validationErrors?.services?.message} />
              </InlineMessage>
            </div>
          )}
        </div>
        {services.length > 0 && (
          <div className={style['live-rate-section']}>
            <div className={style['service-list']}>
              {services.map((service, index) => (
                <RemovableService
                  key={service.serviceId}
                  onRemove={() => onServiceDisplayRemoveCallback(index)}
                  onSellerProviderIdChange={(sellerProviderId) => onSellerProviderIdChangeCallback(index, sellerProviderId)}
                  sellerProviderId={service.sellerProviderId}
                  serviceId={service.serviceId}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={style['section-title']}>
        <FormattedMessage {...messages.flatRateConfiguration} />
      </div>
      <div className={style['section-subtitle']}>
        <FormattedMessage {...messages.flatRateDescription} />
      </div>
      <CurrencyInput
        className={style['currency-input']}
        currencyCode={sellerCurrencyCode}
        onChange={onFlatRateInputChangeCallback}
        onlyPositive
        value={flatRateValue}
      />
      <div className={style['section-title']}>
        <FormattedMessage {...messages.pudoFreeTitle} />
      </div>
      <div className={style['section-subtitle']}>
        <FormattedMessage {...messages.pudoFreeDescription} />
      </div>
      <CurrencyInput
        className={style['currency-input']}
        currencyCode={sellerCurrencyCode}
        onChange={onFeeThresholdInputChangeCallback}
        onlyPositive
        value={feeThresholdValue}
      />
    </div>
  );
};

PudoRateOptions.propTypes = {
  flatRateValue: PropTypes.number,
  feeThresholdValue: PropTypes.number,
  item: DeliveryOptionsItemPropType.isRequired,
  setItem: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    services: PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string,
    }),
  }),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      sellerProviderId: PropTypes.string,
      serviceId: PropTypes.string,
    }),
  ),
  pudoServices: PropTypes.arrayOf(ShippingServicePropType),
};
